/**
 * autoResolution.ts — Device + connection heuristic for AUTO resolution preset
 *
 * BETA 16.64 — Per Dylan: "how come we dont have a way to set resoltuion
 * like 1080p, 2k, 4k, 720p, and it auto chose based on internet connection
 * and detection on how poerful the device you are playing on?"
 *
 * Browser games can't change actual display resolution (that's the OS),
 * but we CAN render the canvas at a specific bitmap size and let CSS
 * scale it. This module picks a sensible default given:
 *   - navigator.hardwareConcurrency  (CPU thread count)
 *   - navigator.deviceMemory         (RAM in GB, Chrome/Android only)
 *   - navigator.connection.effectiveType ('slow-2g'/'2g'/'3g'/'4g')
 *   - navigator.userAgent            (mobile vs desktop sniff)
 *   - viewport size                  (no point rendering 4K on a phone)
 *
 * Heuristic is intentionally conservative — better to default to a
 * lower preset and let the user bump it up than start at 4K and watch
 * the game stutter on a Chromebook.
 */

export type ResolutionPreset = 'auto' | '720p' | '1080p' | '1440p' | 'native';

const PRESET_TARGETS: Record<Exclude<ResolutionPreset, 'auto'>, { w: number; h: number }> = {
  '720p':   { w: 1280,  h: 720  },
  '1080p':  { w: 1920,  h: 1080 },
  '1440p':  { w: 2560,  h: 1440 },
  'native': { w: 99999, h: 99999 },  // effectively no cap — match viewport
};

/**
 * Compute the actual canvas-bitmap scale factor for a preset given the
 * current viewport size. Returns 1.0 for 'native' or any preset whose
 * pixel target equals/exceeds the viewport. Returns <1.0 when the
 * preset is smaller than the viewport (renders at lower res, CSS upscales).
 *
 * Caps at 1.0 to avoid supersampling — a player on a 720p monitor
 * picking '1080p' should NOT make the GPU render 1080p just to downscale,
 * the visible result is identical to 720p.
 */
export function presetToScale(preset: ResolutionPreset, viewportW: number, viewportH: number): number {
  if (preset === 'auto') {
    // Auto resolves to a concrete preset first, then to a scale.
    return presetToScale(detectAutoPreset(viewportW, viewportH), viewportW, viewportH);
  }
  const target = PRESET_TARGETS[preset];
  const scaleW = target.w / viewportW;
  const scaleH = target.h / viewportH;
  const scale = Math.min(scaleW, scaleH);
  return Math.max(0.5, Math.min(1.0, scale));
}

/**
 * Heuristic device classifier. Returns 'low' / 'medium' / 'high' based
 * on hardware hints. NOT exact — these APIs are imperfect and some
 * browsers don't expose them all. The score is conservative.
 */
function classifyDevicePower(): 'low' | 'medium' | 'high' {
  const cores = navigator.hardwareConcurrency ?? 4;
  // navigator.deviceMemory is only present in Chromium-family browsers.
  // Returns approximate GB rounded to one of [0.25, 0.5, 1, 2, 4, 8].
  const memGB = (navigator as unknown as { deviceMemory?: number }).deviceMemory ?? 4;
  // Mobile UA hint — mobile devices generally hit thermal throttling
  // before desktops do, so we de-rate them one tier.
  const ua = navigator.userAgent.toLowerCase();
  const isMobile = /android|iphone|ipad|ipod|mobile/.test(ua);

  let score = 0;
  if (cores >= 8) score += 2;
  else if (cores >= 4) score += 1;

  if (memGB >= 8) score += 2;
  else if (memGB >= 4) score += 1;

  if (isMobile) score = Math.max(0, score - 1);

  if (score >= 3) return 'high';
  if (score >= 1) return 'medium';
  return 'low';
}

/**
 * Network classifier. Most relevant for "should I cap quality even
 * if device is fast" decisions. Largely future-facing — the actual
 * render cost is unaffected by network, but if we ever stream
 * higher-res asset variants this gates that. For now we use it as
 * a soft cap: 2g/slow-2g → never go above 720p regardless of device.
 */
function classifyNetwork(): 'slow' | 'normal' {
  const conn = (navigator as unknown as { connection?: { effectiveType?: string; saveData?: boolean } }).connection;
  if (!conn) return 'normal';
  if (conn.saveData) return 'slow';
  if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return 'slow';
  return 'normal';
}

/**
 * Pick the recommended preset for AUTO mode. Considers device power +
 * network + viewport dimensions (no point targeting 1440p on a 720p
 * window — the cap would just resolve to 1.0 scale anyway).
 */
export function detectAutoPreset(viewportW: number, viewportH: number): Exclude<ResolutionPreset, 'auto'> {
  // BETA 16.97 — Per Dylan: "auto chose native resolution for anyone
  // playing." Previous heuristic (cycle 16.64) was conservative — it
  // capped by device-power class, so even a "medium" device on a
  // 1440p monitor got 1080p render upscaled (visibly blurry). Now
  // AUTO maps to the smallest preset that meets-or-exceeds the
  // ACTUAL viewport, giving a 1.0 render scale on every device. The
  // game's per-frame cost is dominated by entity count + particle
  // count, not pixel count, and BETA 16.91-16.96 shipped meaningful
  // perf wins (spawn rebalance halved entity count, damage-number
  // pool eliminated GC pressure, glow-skip + coalescing). If a real
  // low-end device chokes on native render, the user can manually
  // pick a lower preset.
  void viewportW;
  if (viewportH <= 780)  return '720p';
  if (viewportH <= 1100) return '1080p';
  if (viewportH <= 1500) return '1440p';
  return 'native';
}

/**
 * Human-readable summary of the AUTO decision — surfaced in Settings
 * so the player can see WHY auto picked what it did. Helps diagnose
 * "why is my game blurry?" complaints.
 */
export function describeAutoChoice(viewportW: number, viewportH: number): string {
  const pick = detectAutoPreset(viewportW, viewportH);
  // BETA 16.97 — AUTO is now viewport-only (native-to-screen). Power
  // and network classifiers preserved for diagnostic visibility but
  // no longer influence the pick.
  const power = classifyDevicePower();
  const net = classifyNetwork();
  return `${pick} (native to ${viewportW}×${viewportH}; power: ${power}; net: ${net})`;
}
